import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register } from '../../services/auth';
import { getAdminUnitsPublic } from '../../services/adminUnits';
import { User, Mail, Phone, Lock, CreditCard, Church, ArrowRight, Loader2, AlertCircle, ChevronLeft } from 'lucide-react';

// Password validation matching backend policy
const validatePassword = (password) => {
  if (password.length < 6) return 'Password must be at least 6 characters long';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return null;
};

const Register = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    id_number: '',
    password: '',
    admin_unit_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const [level1Units, setLevel1Units] = useState([]);
  const [level2Units, setLevel2Units] = useState([]);
  const [selectedLevel1, setSelectedLevel1] = useState('');
  const [selectedLevel2, setSelectedLevel2] = useState('');
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [unitsError, setUnitsError] = useState(false);

  const orgCode = 'NYM'; // Change as needed (or fetch from user input)

  // Fetch level 1 units on mount
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await getAdminUnitsPublic({ organization_id: 2 });
        
        console.log('Production Admin Units Raw Response:', response);

        // Normalize response to array
        const units = Array.isArray(response) ? response : (response?.data || response?.units || []);
        
        // Use loose type matching by checking string values to fix cPanel numeric string variation
        const filteredLevel1 = units.filter(u => {
          const level = u?.level ?? u?.Level;
          return level?.toString() === '1';
        });

        setLevel1Units(filteredLevel1);
        setUnitsError(false);
      } catch (err) {
        console.error('Error fetching units:', err);
        setUnitsError(true);
        setError('Unable to load church structure. Please enter your code manually.');
      } finally {
        setLoadingUnits(false);
      }
    };
    fetchUnits();
  }, []);

  // Fetch level 2 units when a level 1 is selected
  useEffect(() => {
    if (!selectedLevel1 || unitsError) {
      setLevel2Units([]);
      return;
    }
    const fetchLevel2 = async () => {
      try {
        const response = await getAdminUnitsPublic({ parent_id: selectedLevel1, organization_id: 2 });
        
        const units = Array.isArray(response) ? response : (response?.data || response?.units || []);

        setLevel2Units(units);
        setSelectedLevel2('');
      } catch (err) {
        console.error('Error fetching level 2 units:', err);
        setError('Unable to load churches for this meeting');
      }
    };
    fetchLevel2();
  }, [selectedLevel1, unitsError]);

  // Update admin_unit_code when level2 is selected
  useEffect(() => {
    if (selectedLevel2) {
      const selectedUnit = level2Units.find(u => {
        const unitId = u?.id ?? u?.ID;
        return unitId?.toString() === selectedLevel2.toString();
      });
      
      if (selectedUnit) {
        const unitCode = selectedUnit?.code ?? selectedUnit?.Code;
        setFormData(prev => ({ ...prev, admin_unit_code: unitCode }));
      }
    }
  }, [selectedLevel2, level2Units]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate admin_unit_code
    if (!formData.admin_unit_code) {
      setError('Please select a Local Church or enter a valid code');
      return;
    }
    
    // Validate password
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await register({ ...formData, organization_code: orgCode });
      setSuccess(true);
      navigate(`/payment/register/${data.memberId}`, { state: { paymentId: data.paymentId } });
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
      setLoading(false);
    }
  };

  // Show loading spinner after successful registration
  if (success) {
    return (
      <div className="auth-split-container flex items-center justify-center bg-slate-50">
        <div className="text-center p-12 bg-white rounded-3xl shadow-xl max-w-md animate-in">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-blue-50 rounded-full text-blue-600">
              <Loader2 className="animate-spin" size={48} />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-nym-navy">Redirecting to Payment</h2>
          <p className="text-slate-500 mt-2">Preparing your registration invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-split-container flex flex-col md:flex-row min-h-screen bg-slate-50">
      {/* LEFT SIDEBAR (hidden on mobile, visible on md+) */}
      <div className="auth-sidebar register-sidebar hidden md:flex md:w-1/3 bg-gradient-to-br from-nym-navy to-nym-blue text-white p-10 flex-col justify-between">
        <div>
          <Link to="/login" className="back-link inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-8">
            <ChevronLeft size={18} /> Back to Login
          </Link>
          <div className="brand-badge inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-xs font-bold tracking-wider mb-6">
            Member Enrollment
          </div>
          <h1 className="text-4xl font-bold leading-tight">Join NYM Welfare</h1>
          <p className="text-white/80 mt-4 text-lg">Register to manage your contributions and access church welfare services digitally.</p>
        </div>
        <div className="auth-sidebar-footer text-xs opacity-50">
          © 2026 Nairobi Yearly Meeting
        </div>
      </div>

      {/* RIGHT SIDE – Form (full width on mobile) */}
      <div className="auth-form-section flex-1 bg-white md:bg-transparent p-6 md:p-12 overflow-y-auto">
        <div className="register-form-wrapper max-w-2xl mx-auto py-4 md:py-12">
          {/* Mobile logo */}
          <div className="md:hidden flex justify-center mb-6">
            <span className="text-2xl font-black text-nym-navy">NYM</span>
          </div>
          
          <h2 className="text-3xl font-black text-nym-navy mb-2 text-center md:text-left">Create Account</h2>
          <p className="text-slate-500 mb-8 text-center md:text-left">Enter your details to join the welfare program</p>

          {error && (
            <div className="error-banner mb-6 flex items-start gap-3 p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">
              <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm font-semibold">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="modern-form space-y-6">
            {/* Personal Details - responsive grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="input-group">
                <label className="block text-sm font-bold text-slate-700 mb-1">Full Name</label>
                <div className="input-wrapper relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    name="full_name"
                    type="text"
                    placeholder="Full Name"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="input-group">
                <label className="block text-sm font-bold text-slate-700 mb-1">Email Address</label>
                <div className="input-wrapper relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    name="email"
                    type="email"
                    placeholder="Email"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="input-group">
                <label className="block text-sm font-bold text-slate-700 mb-1">Phone Number</label>
                <div className="input-wrapper relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    name="phone"
                    type="tel"
                    placeholder="2547..."
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="input-group">
                <label className="block text-sm font-bold text-slate-700 mb-1">ID Number</label>
                <div className="input-wrapper relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    name="id_number"
                    type="text"
                    placeholder="ID Number"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Password */}
            <div className="input-group">
              <label className="block text-sm font-bold text-slate-700 mb-1">Password</label>
              <div className="input-wrapper relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  onChange={handleChange}
                  required
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                Must be at least 6 characters, contain uppercase, lowercase and a number.
              </p>
            </div>

            {/* Church Affiliation Panel */}
            <div className="church-selection-panel mt-4 p-6 bg-slate-50 rounded-2xl border border-slate-200">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                <Church size={14} /> Church Affiliation
              </h3>

              {loadingUnits ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                  <Loader2 className="animate-spin" size={16} /> Loading units...
                </div>
              ) : unitsError ? (
                <div className="input-group">
                  <label className="block text-sm font-bold text-slate-700 mb-1">Local Church Code</label>
                  <div className="input-wrapper relative">
                    <Church className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      name="admin_unit_code"
                      type="text"
                      placeholder="Enter Local Church Code"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Monthly Meeting</label>
                    <select
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white"
                      value={selectedLevel1}
                      onChange={(e) => setSelectedLevel1(e.target.value)}
                      required
                    >
                      <option value="">Select Meeting</option>
                      {level1Units.length === 0 ? (
                        <option value="" disabled>No meetings found</option>
                      ) : (
                        level1Units.map(u => {
                          const id = u?.id ?? u?.ID;
                          const name = u?.name ?? u?.Name;
                          return <option key={id} value={id}>{name}</option>;
                        })
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Local Church</label>
                    <select
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white"
                      value={selectedLevel2}
                      onChange={(e) => setSelectedLevel2(e.target.value)}
                      required
                      disabled={!selectedLevel1}
                    >
                      <option value="">Select Church</option>
                      {level2Units.map(u => {
                        const id = u?.id ?? u?.ID;
                        const name = u?.name ?? u?.Name;
                        return <option key={id} value={id}>{name}</option>;
                      })}
                    </select>
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="nym-primary-btn w-full bg-nym-blue text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  <span>Complete Enrollment</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="text-center mt-8 text-sm text-slate-500">
            Already registered? <Link to="/login" className="text-nym-blue font-bold hover:underline">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;